// ============================================================================
// SegmentPopover.jsx
// The detail for a clicked timeline segment, floating next to the segment
// itself.
//
// It used to render in a fixed slot — below the card in the shift list, inside
// the card in the driver drill-down — so the answer appeared somewhere other
// than where you clicked, and on a long list you had to scroll away to read it
// and scroll back to click the next one. Anchoring it to the click keeps the
// question and the answer in the same place.
//
// ── Why a portal ────────────────────────────────────────────────────────────
// The card wrapping the timeline sets `overflow: hidden`, which is right for
// its rounded corners and wrong for anything that needs to escape it: a
// popover positioned inside would simply be clipped at the card edge. So this
// renders into document.body and positions itself in DOCUMENT coordinates
// (rect + scrollY), not viewport ones — that way it travels with the page on
// scroll instead of detaching from its anchor the moment anyone moves.
//
// One popover exists at a time, shared by both timelines, so the two can never
// drift into showing different fields for the same click.
// ============================================================================

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, LogIn, LogOut, MapPin, X } from 'lucide-react';
import { api } from '../../services/api';
import { c, t, sp, r, card, btn, chart } from '../../theme';
import { fmtDate, fmtTimePT, fmtMiles, fmtPct, fmtMph, splitWorkerName } from './format';
import { fmtMin, visitColor, siteTypeLabel } from './timeline';

const WIDTH = 430;
const GAP = 10;        // breathing room between the segment and the panel
const MARGIN = 10;     // keep this far from the viewport edges

export default function SegmentPopover({ selected, thresholdMin = 45, preThresholdMin = 60, onClose, onReviewed }) {
  const { kind, row, geo, meal, visit, anchorRect } = selected;
  const pt = geo?.post_trip;

  const ref = useRef(null);
  const [pos, setPos] = useState(null);   // null until measured, so it never flashes in the wrong place

  // Measure the rendered panel, then place it. Guessing the height and
  // correcting afterwards produces a visible jump on every open.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchorRect) return;
    const h = el.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;

    // Horizontal: centre on the segment, then clamp inside the viewport.
    let left = anchorRect.left + anchorRect.width / 2 - WIDTH / 2;
    left = Math.max(MARGIN, Math.min(left, vw - WIDTH - MARGIN));

    // Vertical: below the segment by default, above it when that would run off
    // the bottom. If neither fits, sit against the top edge and let the panel
    // scroll internally rather than overflow the page.
    const spaceBelow = vh - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    let top;
    if (spaceBelow >= h + GAP + MARGIN || spaceBelow >= spaceAbove) {
      top = anchorRect.bottom + GAP;
    } else {
      top = anchorRect.top - h - GAP;
    }
    top = Math.max(MARGIN, Math.min(top, vh - Math.min(h, vh - 2 * MARGIN) - MARGIN));

    // Document coordinates, so the panel scrolls with the row it belongs to.
    setPos({ left: left + window.scrollX, top: top + window.scrollY });
  }, [anchorRect, kind, meal?.id, visit?.id]);

  // Escape closes, and a click anywhere outside closes — the two things people
  // try first when a floating panel is in the way.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // Deferred a tick: the click that opened this popover is still propagating,
    // and without the delay it closes itself immediately.
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      clearTimeout(id);
    };
  }, [onClose]);

  useEffect(() => { ref.current?.focus(); }, []);

  const [note, setNote] = useState(meal?.review_note || '');
  const reviewMutation = useMutation({
    mutationFn: (review_status) =>
      api.patch(`/time-wage-audit/discrepancies/${meal.id}/review`, { review_status, review_note: note }),
    onSuccess: () => { toast.success('Review saved.'); onReviewed?.(); onClose(); },
    onError:   (err) => toast.error(err.message || 'Failed to save review.'),
  });
  const busy = reviewMutation.isPending;

  const driver = splitWorkerName(row.worker_name, row.employee_id);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={kind === 'meal' ? 'Meal break gap detail'
        : kind === 'visit' ? 'Site visit detail'
        : kind === 'startshift' ? 'Start of shift detail' : 'End of shift detail'}
      tabIndex={-1}
      style={{
        ...s.pop,
        left: pos ? `${pos.left}px` : '-9999px',
        top:  pos ? `${pos.top}px`  : '-9999px',
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div style={s.head}>
        <span style={s.title}>
          {kind === 'meal'
            ? <><AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Meal break gap</>
            : kind === 'visit'
              // The dot carries the site's own colour, so the panel and the
              // mark that opened it are visibly the same thing.
              ? <><MapPin size={13} style={{ verticalAlign: -2, color: visitColor(chart, visit?.site_type) }} /> {visit?.site || 'Site visit'}</>
              : kind === 'startshift'
                ? <><LogIn size={13} style={{ verticalAlign: -2 }} /> Start of shift</>
                : <><LogOut size={13} style={{ verticalAlign: -2 }} /> End of shift</>}
        </span>
        <button style={s.close} onClick={onClose} aria-label="Close"><X size={14} /></button>
      </div>
      <div style={s.sub}>
        {driver.name}<span style={s.subId}>ID {driver.id}</span> · {fmtDate(geo.date)}
      </div>

      {kind === 'visit' ? (
        <>
          <div style={s.grid}>
            <KV k="Arrived" v={fmtTimePT(visit.arrived)} />
            <KV k="Departed" v={visit.departed ? fmtTimePT(visit.departed) : 'not recorded'} />
            <KV k="On site" v={fmtMin(visit.minutes)} />
            <KV k="Stop" v={`#${visit.visit_seq} of the shift`} />
            <KV k="Site type" v={siteTypeLabel(visit.site_type)} />
            <KV k="Top speed" v={visit.max_speed_mph == null ? '—' : `${Number(visit.max_speed_mph).toFixed(1)} mph`} />
            <KV k="Vehicle" v={visit.vehicle_name || '—'} />
          </div>
          <div style={s.note}>
            {visit.departed
              ? <>A geofence entry and exit, as the producer detected them. Time on site is
                  theirs, not recomputed here — the same rule the lapse figures follow, so
                  our number cannot drift from the one their pipeline reports.</>
              : <><strong>No departure was recorded.</strong> The driver was seen entering
                  this site and not leaving it, so the mark is a point rather than a span.
                  An end time is not invented for it.</>}
          </div>
        </>
      ) : kind === 'meal' ? (
        <>
          <div style={s.grid}>
            <KV k="Gap" v={`${fmtTimePT(meal.gap_start)} – ${fmtTimePT(meal.gap_end)}`} />
            {/* gap_minutes is NUMERIC, so pg hands it over as "30.00". */}
            <KV k="Duration" v={`${Number(meal.gap_minutes)} min`} />
            <KV k="Vehicle" v={meal.vehicle_name || '—'} />
            <KV k="Distance" v={fmtMiles(meal.miles)} />
            <KV k="Moving" v={fmtPct(meal.pct_moving)} />
            <KV k="Avg speed" v={fmtMph(meal.avg_mph)} />
            <KV k="Engine" v={meal.engine_state || '—'} />
            <KV k="Source" v={meal.source_status} />
          </div>
          <div style={s.note}>
            {Number(meal.pct_moving) >= 90
              ? <>Moved across {fmtPct(meal.pct_moving)} of the break — the source system calls that
                  unambiguous
                  {meal.engine_state === 'unknown' && <>, <strong>but engine state is unknown</strong>, so
                    the evidence is thinner than the number suggests</>}.</>
              : <>Moved across {fmtPct(meal.pct_moving)} of the break — ambiguous, and as consistent with
                  a food run as with continued work.</>}
            {' '}A discrepancy between two systems, not an adjudicated violation.
          </div>

          <label style={s.label}>Reviewer note</label>
          <textarea
            style={s.textarea} rows={2} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Record what was checked — dispatch log, driver statement…"
          />
          <div style={s.actions}>
            <button style={s.resetBtn}   disabled={busy} onClick={() => reviewMutation.mutate('unreviewed')}>Reset</button>
            <button style={s.dismissBtn} disabled={busy} onClick={() => reviewMutation.mutate('dismissed')}>Dismiss</button>
            <button style={s.confirmBtn} disabled={busy} onClick={() => reviewMutation.mutate('confirmed')}>Confirm</button>
          </div>
          {meal.review_status && meal.review_status !== 'unreviewed' && (
            <div style={s.meta}>Currently {meal.review_status}.</div>
          )}
        </>
      ) : kind === 'startshift' ? (
        <>
          {/* The mirror of the end-of-shift panel below: same shift, same row,
              the other three timestamps. Deliberately no review actions — the
              post-trip end has none either, because the threshold that would
              make an adjudication queue meaningful is still provisional. */}
          <div style={s.grid}>
            <KV k="Punched in" v={fmtTimePT(pt.shift_start)} />
            <KV k="Yard" v={pt.departure_yard_name || '—'} />
            <KV k="Pre-trip submitted" v={fmtTimePT(pt.pre_inspection_submitted)} />
            <KV k="Inspection took" v={fmtMin(pt.pre_inspection_minutes)} />
            <KV k="Left yard" v={fmtTimePT(pt.yard_departure)} />
            <KV k="Clock-in to departure" v={fmtMin(pt.clockin_to_departure_min)} />
            <KV k="Unaccounted" v={fmtMin(pt.pre_unaccounted_min)}
                strong={Number(pt.pre_unaccounted_min) > preThresholdMin} />
            <KV k="Failed items" v={pt.pre_inspection_failed_items ?? '—'} />
          </div>
          <div style={s.note}>
            {pt.pre_category === 'measured'
              ? <>Unaccounted is time on the clock at the yard before the truck leaves that is
                  neither inspection nor travel. The pre-trip walk-around, fuelling, waiting for a
                  bay and yard congestion all live in this window, so a normal start is not zero.</>
              : pt.pre_category === 'form_left_open'
                ? <><strong>Not a reliable reading.</strong> The inspection form was open across an
                    implausible span, which puts this row in the same bucket as the post-trip
                    detection residue — in the profiled export these sit a median of ten hours
                    before their own clock-in. Shown for completeness, not for action.</>
                : pt.pre_completeness === 'departure_before_shift'
                  ? <><strong>The truck left the yard before the shift's first punch.</strong> The
                      front-end inverse of punching out before returning, and potentially unpaid
                      driving.</>
                  : pt.pre_completeness === 'no_yard_departure'
                    ? <>No yard departure was detected for this shift, so there is no window to
                        measure. The punch and the inspection may both be present — absence here is
                        missing telematics, not a clean start.</>
                    : pt.pre_completeness === 'no_pre_inspection'
                      ? <>No pre-trip inspection was found for this shift. That is a coverage gap in
                          the Fleetio feed as often as it is a missing inspection, so it is reported
                          rather than counted.</>
                      : <>This shift carries no measurable pre-trip window — a punch with no usable
                          yard or inspection data behind it. It reads blank rather than zero.</>}
          </div>
        </>
      ) : (
        <>
          <div style={s.grid}>
            <KV k="Back at yard" v={fmtTimePT(pt.yard_arrival)} />
            <KV k="Yard" v={pt.yard_name || '—'} />
            <KV k="Inspection submitted" v={fmtTimePT(pt.inspection_submitted)} />
            <KV k="Inspection took" v={fmtMin(pt.inspection_minutes)} />
            <KV k="Punched out" v={fmtTimePT(pt.shift_end)} />
            <KV k="Arrival to clock-out" v={fmtMin(pt.arrival_to_clockout_min)} />
            <KV k="Unaccounted" v={fmtMin(pt.unaccounted_min)}
                strong={Number(pt.unaccounted_min) > thresholdMin} />
            <KV k="Failed items" v={pt.inspection_failed_items ?? '—'} />
            {Number(pt.inspection_form_count) > 1 && (
              <KV k="Inspections" v={`${pt.inspection_form_count} forms`} />
            )}
          </div>
          <div style={s.note}>
            {pt.category === 'measured'
              ? <>Unaccounted is time back at the yard that is neither travel nor inspection.
                  Parking, walking in, fuelling, washing and handing in keys all live in this window,
                  so a normal close-out is not zero.</>
              : pt.category === 'out_before_return'
                ? <><strong>Punched out before the truck reached the yard</strong> — the inverse finding,
                    and potentially unpaid driving.</>
                /* The common unmeasurable case, and the one that reads as a
                   missing feature rather than missing data: the inspection IS
                   here, so "no close-out" looks like a bug in the page. It is
                   not. The inspection comes from Fleetio against a vehicle;
                   the arrival comes from Samsara's geofence. When those do not
                   resolve to the same vehicle there is no arrival to measure
                   from, and the shift is unmeasurable at this end.

                   Worth naming the form count where there is more than one:
                   a driver inspecting a tractor AND its trailer is 96% of
                   Transfer Driver shifts, and that population carries this gap
                   far more than any other (16.9% of their shifts get an
                   arrival, against 62.5% for Residential). */
                : pt.completeness === 'no_yard_arrival' && pt.inspection_submitted
                  ? <>
                      <strong>No close-out to measure</strong> — the inspection was recorded
                      {pt.vehicle_name ? <> against vehicle <strong>{pt.vehicle_name}</strong></> : null},
                      but no yard arrival was matched to it, so there is no arrival time to
                      measure from.
                      {Number(pt.inspection_form_count) > 1 && (
                        <> This shift submitted <strong>{pt.inspection_form_count} inspections</strong>
                          {/\+/.test(String(pt.inspection_form_title || ''))
                            ? <> ({String(pt.inspection_form_title).split('+').map((x) => x.trim()).join(' · ')})</>
                            : null}, which is the usual shape when a driver takes a tractor and a
                          trailer.</>
                      )}
                      {' '}This is missing source data, not a clean shift.
                    </>
                  : <>No computable close-out — {String(pt.completeness || '').replace(/_/g, ' ')}.</>}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}

function KV({ k, v, strong }) {
  return (
    <div>
      <div style={s.kvK}>{k}</div>
      <div style={{ ...s.kvV, color: strong ? c.dangerText : c.text, fontWeight: strong ? 650 : 500 }}>{v}</div>
    </div>
  );
}

const s = {
  pop: {
    position: 'absolute', zIndex: 60, width: WIDTH,
    maxHeight: `calc(100vh - ${MARGIN * 2}px)`, overflowY: 'auto',
    ...card.base,
    boxShadow: '0 10px 30px rgba(17,24,39,.16), 0 2px 8px rgba(17,24,39,.10)',
    padding: sp[4],
  },
  head:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { ...t.h3, color: c.text, display: 'inline-flex', alignItems: 'center', gap: 6 },
  sub:   { ...t.caption, color: c.textMuted, marginBottom: sp[3] },
  subId: { ...t.micro, ...t.tabular, color: c.textFaint, marginLeft: 6 },
  close: { ...btn.icon },

  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: sp[3] },
  kvK:  { ...t.label, color: c.textFaint, marginBottom: 2 },
  kvV:  { ...t.bodySm, ...t.tabular },

  note: { ...t.caption, color: c.textStrong, lineHeight: 1.5, marginTop: sp[3],
          paddingTop: sp[3], borderTop: `1px solid ${c.border}` },

  label:    { ...t.label, color: c.textMuted, display: 'block', marginTop: sp[3], marginBottom: 4 },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontFamily: 'inherit',
              fontSize: '13px', color: c.textStrong, border: `1px solid ${c.borderStrong}`,
              borderRadius: r.md, resize: 'vertical' },
  actions:  { display: 'flex', justifyContent: 'flex-end', gap: sp[2], marginTop: sp[2] },
  resetBtn:   { ...btn.secondaryXs },
  dismissBtn: { ...btn.secondarySm },
  confirmBtn: { ...btn.primarySm },
  meta:  { ...t.micro, color: c.textFaint, marginTop: sp[2] },
};
